import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export const lineDiscountPercent = (item, defaultTiers) => {
  if (item.is_bundle || String(item.product_id || "").startsWith("bundle:")) return 0;
  const tiers = item.volume_tiers?.length ? item.volume_tiers : defaultTiers || [];
  const applicable = tiers.filter((t) => item.quantity >= t.min_qty);
  if (!applicable.length) return 0;
  return applicable.reduce((m, t) => (t.min_qty > m.min_qty ? t : m)).percent;
};

// Mirrors backend shipping_fee_for(): manual = configured price always; automatic = tier by unit count, Standard free at/over threshold
export const shippingFeeFor = (cfg, method, merch, units) => {
  const flat = method === "express" ? cfg.expressFee : cfg.fee;
  if (cfg.mode === "manual") return flat;
  const tiers = (cfg.table || []).filter((t) => units >= t.min_qty);
  const fee = tiers.length ? tiers.reduce((m, t) => (t.min_qty > m.min_qty ? t : m))[method] : flat;
  if (method === "standard" && merch >= cfg.threshold) return 0;
  return fee;
};

export const cartUnits = (items) => items.reduce((s, i) => s + i.quantity * (i.units_per_item || 1), 0);

const DEFAULT_SHIP_CFG = { mode: "automatic", table: [], threshold: 300, fee: 20, expressFee: 30, expressEnabled: true, standardEta: "2–8 business days", expressEta: "1–2 business days" };

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cart") || "[]");
    } catch {
      return [];
    }
  });
  const [tiers, setTiers] = useState([]);
  const [shipCfg, setShipCfg] = useState(DEFAULT_SHIP_CFG);
  const [bundlesVisible, setBundlesVisible] = useState(false);
  const syncedUserRef = useRef(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setTiers(data.volume_discounts || []);
      setBundlesVisible(!!data.bundles_visible);
      setShipCfg({
        mode: data.shipping_mode || "automatic",
        table: data.shipping_rate_table || [],
        threshold: data.free_shipping_threshold ?? 300,
        fee: data.shipping_fee ?? 20,
        expressFee: data.express_shipping_fee ?? 30,
        expressEnabled: data.express_shipping_enabled ?? true,
        standardEta: data.standard_shipping_eta || "2–8 business days",
        expressEta: data.express_shipping_eta || "1–2 business days",
      });
    }).catch(() => {});
  }, []);

  // Saved cart: pull + merge the server cart once per signed-in user; a sign-out starts the next visitor clean
  useEffect(() => {
    if (!user) {
      if (syncedUserRef.current) setItems([]);
      syncedUserRef.current = null;
      hydratedRef.current = false;
      return;
    }
    if (syncedUserRef.current === user.user_id) return;
    syncedUserRef.current = user.user_id;
    api.get("/cart").then(({ data }) => {
      const server = Array.isArray(data.items) ? data.items : [];
      setItems((prev) => {
        const merged = [...prev];
        for (const s of server) {
          if (s?.product_id && !merged.find((i) => i.product_id === s.product_id)) merged.push(s);
        }
        return merged;
      });
      hydratedRef.current = true;
    }).catch(() => { hydratedRef.current = true; });
  }, [user]);

  // Saved cart: push changes (debounced) so the cart follows the account
  useEffect(() => {
    if (!user || !hydratedRef.current) return;
    const t = setTimeout(() => {
      api.put("/cart", { items }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [items, user]);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [...prev, {
        product_id: product.id, name: product.name, dosage: product.dosage,
        price: product.price, image_url: product.image_url, quantity,
        volume_tiers: product.volume_tiers || [],
      }];
    });
  }, []);

  const addBundle = useCallback((bundle, quantity = 1) => {
    setItems((prev) => {
      const pid = `bundle:${bundle.id}`;
      const existing = prev.find((i) => i.product_id === pid);
      if (existing) {
        return prev.map((i) => (i.product_id === pid ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, {
        product_id: pid, name: bundle.name, dosage: "Bundle",
        price: bundle.bundle_price,
        image_url: bundle.image_url || bundle.components_detail?.[0]?.image_url,
        quantity, volume_tiers: [], is_bundle: true,
        units_per_item: Math.max(1, (bundle.components || []).reduce((s, c) => s + (c.quantity || 1), 0)),
      }];
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) return removeItem(productId);
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i)));
  }, [removeItem]);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

  const pricing = useMemo(() => {
    let discountAmount = 0;
    for (const it of items) {
      const pct = lineDiscountPercent(it, tiers);
      if (pct > 0) discountAmount += +(it.price * it.quantity * pct / 100).toFixed(2);
    }
    discountAmount = +discountAmount.toFixed(2);
    const merch = +(subtotal - discountAmount).toFixed(2);
    const units = cartUnits(items);
    const ship = items.length > 0 ? shippingFeeFor(shipCfg, "standard", merch, units) : 0;
    const freeShipEnabled = shipCfg.mode !== "manual";
    return {
      discountAmount,
      merchTotal: merch,
      units,
      shipping: ship,
      expressShipping: items.length > 0 ? shippingFeeFor(shipCfg, "express", merch, units) : 0,
      total: +(merch + ship).toFixed(2),
      amountToFreeShip: freeShipEnabled && ship > 0 ? +(shipCfg.threshold - merch).toFixed(2) : 0,
      freeShipThreshold: shipCfg.threshold,
      freeShipEnabled,
    };
  }, [tiers, items, subtotal, shipCfg]);

  const value = useMemo(
    () => ({ items, addItem, addBundle, updateQuantity, removeItem, clearCart, subtotal, count, tiers, bundlesVisible, shipCfg, ...pricing }),
    [items, addItem, addBundle, updateQuantity, removeItem, clearCart, subtotal, count, tiers, bundlesVisible, shipCfg, pricing]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
